import { reactQueryPost } from "@circle-react/helpers/backendRequestHelpers";
import { spaceGroupPaths } from "@circle-react/helpers/urlHelpers";
import { useBoolean } from "@circle-react/hooks/utils/useBoolean";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useState } from "react";
import { useMutation } from "react-query";

import { useLgScreenMediaQuery } from "@/react/hooks/useMediaQuery";
import { useThemeContext } from "@/react/providers/Theme";

import type { SpaceGroupWithSpaces } from "../../Sidebar";
import { CollapsibleSection } from "../CollapsibleSection";
import { AddMenu } from "./AddMenu";
import { OptionsMenu } from "./OptionsMenu";
import { SpaceList } from "./SpaceList";
import { useSpaceGroupAccessControl } from "./useSpaceGroupAccessControl";

export interface SpaceGroupSectionProps {
  isPreview?: boolean;
  position: number;
  spaceGroup: SpaceGroupWithSpaces;
}

export const SpaceGroupSection = ({
  isPreview = false,
  position,
  spaceGroup,
}: SpaceGroupSectionProps) => {
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [isCollapsed, toggleIsCollapsed] = useBoolean(spaceGroup.collapsed);
  const { canAdd, canManage, canCreateSpace, isAdmin } =
    useSpaceGroupAccessControl(spaceGroup);
  const { currentAppearance } = useThemeContext();
  const isLargeScreen = useLgScreenMediaQuery();

  const { mutate: toggleVisibility } = useMutation({
    mutationFn: () =>
      reactQueryPost(
        spaceGroupPaths.toggleVisibility({
          spaceGroupId: String(spaceGroup.id),
        }),
      ),
  });

  const handleCollapseToggle = () => {
    toggleIsCollapsed();
    toggleVisibility();
  };

  const isDragEnabled = isAdmin && isLargeScreen;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    setActivatorNodeRef,
    isDragging,
  } = useSortable({
    id: `space-group-${spaceGroup.id}`,
    disabled: !isDragEnabled,
    data: {
      type: "spaceGroup",
      id: spaceGroup.id,
    },
  });

  const sortableStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <CollapsibleSection
      ref={setNodeRef}
      id={`space-group-${spaceGroup.slug}`}
      isDragging={isDragging}
      style={sortableStyle}
      {...attributes}
      {...listeners}
    >
      <CollapsibleSection.Header
        collapseButtonRef={setActivatorNodeRef}
        isActionsOpen={isActionsOpen}
        isCollapsed={isCollapsed}
        isDragEnabled={isDragEnabled}
        isDragging={isDragging}
        title={spaceGroup.name}
        onCollapseToggle={handleCollapseToggle}
        actions={
          !isPreview && (
            <>
              <OptionsMenu
                canManage={canManage}
                spaceGroup={spaceGroup}
                onToggle={setIsActionsOpen}
              />
              {canAdd && (
                <AddMenu
                  canCreateSpace={canCreateSpace}
                  isAdmin={isAdmin}
                  position={position}
                  spaceGroup={spaceGroup}
                  onOpenChange={setIsActionsOpen}
                />
              )}
            </>
          )
        }
      />
      <CollapsibleSection.Content isCollapsed={isCollapsed}>
        <SpaceList
          spaceGroup={spaceGroup}
          currentAppearance={currentAppearance}
          isAdmin={isAdmin}
          isLargeScreen={isLargeScreen}
        />
      </CollapsibleSection.Content>
    </CollapsibleSection>
  );
};
